import { Box, FormControlLabel, InputLabel, Typography } from '@mui/material';
import { DIFFERENT_COLOR, TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import PieChartCommon from 'components/Common/PieChartCommon';
import SkeletonCharts from 'components/Common/SkeletonCharts';
import { SwitchCustom } from 'components/Common/SwitchCustom';
import { useClientSettingsStyle } from 'components/Dashboard/ClientSettings/ClientSettingsStyle';
import { QUERY_KEY } from 'constants/constants';
import { get, isEmpty } from 'lodash';
import randomColor from 'randomcolor';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  getSizeSoilWaterContentFieldAnalytic,
  getSoilWaterContentFieldAnalytic,
} from 'services/analytics/apiAnalyticsConfig.services';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  changeSizeSoilWaterContentFieldList,
  mapViewSelector,
  setDataSoilWaterContentField,
} from 'store/slices/mapViewSlice';
import SoilWaterContentFieldCardLayer from '../../Layer/SoilWaterContentField';
import { SwipeableViewsCustom } from '../../SwipeableViewsCustom';
import InfoTable from '../CropCover/InfoTable';

const SoilWaterContentFieldAnalytics = () => {
  const dispatch = useAppDispatch();
  const [chartMode, setChartMode] = useState(false);
  const classes = useClientSettingsStyle();

  const { analyticId, sizeSoilWaterContentFieldSelected, dataSoilWaterContentField, sizeSoilWaterContentFieldList } =
    useAppSelector(mapViewSelector);

  useQuery(
    [QUERY_KEY.SIZE_SOIL_WATER_CONTENT_FIELD_ANALYTIC, analyticId],
    () => getSizeSoilWaterContentFieldAnalytic(analyticId || ''),
    {
      enabled: !!analyticId && isEmpty(sizeSoilWaterContentFieldList),
      onSuccess: (response) => {
        if (response.data) {
          dispatch(changeSizeSoilWaterContentFieldList(response.data));
        }
      },
    }
  );

  const { data: dataCropType } = useQuery(
    [QUERY_KEY.SOIL_WATER_CONTENT_FIELD_ANALYTIC, analyticId, sizeSoilWaterContentFieldSelected],
    () => getSoilWaterContentFieldAnalytic(analyticId || '', sizeSoilWaterContentFieldSelected + ''),
    { enabled: !!analyticId && !!sizeSoilWaterContentFieldSelected }
  );

  const dataPie = useMemo(() => {
    if (dataSoilWaterContentField) {
      return dataSoilWaterContentField.map((_item: any) => ({
        name: _item.type,
        value: _item.lduseArea,
        totalTree: _item.totalTree,
      }));
    }
    return [];
  }, [dataSoilWaterContentField]);

  const getDataChart = useMemo(
    () =>
      dataSoilWaterContentField?.map((_item: any) => ({
        name: _item.type,
        value: chartMode ? _item.totalTree : _item.lduseArea,
      })),
    [chartMode, dataSoilWaterContentField]
  );

  const hasTreeCount = !!(
    dataSoilWaterContentField?.length && dataSoilWaterContentField.some((item: any) => item.totalTree > 0)
  );

  const colorSoilWaterContentField = useMemo(() => {
    if (dataCropType?.data) {
      let colors: string[] = [];
      dispatch(
        setDataSoilWaterContentField(
          dataCropType.data.map((item: any, index: number) => {
            const colorRandom = randomColor({ luminosity: 'dark' });
            const _color = item.color || get(DIFFERENT_COLOR, `[${index}]`, colorRandom);
            colors.push(_color);
            return {
              ...item,
              visible: true,
              color: _color,
            };
          })
        )
      );
      return colors;
    }
    return [];
  }, [dataCropType, dispatch]);

  return (
    <SwipeableViewsCustom
      isDisableLabel
      analyticName={TYPE_ANALYTICS_MAP_VIEW.SOIL_WATER_CONTENT_FIELD}
      analyticTab={
        <>
          {dataSoilWaterContentField?.length > 0 ? (
            <>
              <PieChartCommon
                title="Soil Water Content Field"
                dataChart={getDataChart}
                colorCharts={colorSoilWaterContentField}
              />
              {hasTreeCount && (
                <>
                  <InputLabel sx={{ marginLeft: '25px', fontSize: '14px' }}>Chart Mode:</InputLabel>
                  <FormControlLabel
                    sx={{ marginLeft: '30px' }}
                    control={
                      <SwitchCustom
                        checked={chartMode}
                        onClick={() => {
                          setChartMode(!chartMode);
                        }}
                      />
                    }
                    label={<Typography className={classes.textSetting}>{chartMode ? 'Tree Count' : 'Area'}</Typography>}
                  />
                </>
              )}
              <Box component={'div'} sx={{ marginTop: '30px' }}>
                <InfoTable dataPie={dataPie} hasTreeCount={hasTreeCount} />
              </Box>
            </>
          ) : (
            <>
              <SkeletonCharts count={1} />
            </>
          )}
        </>
      }
      layerTab={<SoilWaterContentFieldCardLayer labelCard={TYPE_ANALYTICS_MAP_VIEW.SOIL_WATER_CONTENT_FIELD} />}
      isShowDownloadButton={dataSoilWaterContentField?.length > 0}
    />
  );
};

export default SoilWaterContentFieldAnalytics;
